import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab
} from '@mui/material';
import TestGrowthChart from '../charts/TestGrowthChart';
import TestSuiteDistributionChart from '../charts/TestSuiteDistributionChart';
import AutomationStatusTable from '../tables/AutomationStatusTable';
import QuarterlyTestGrowthChart from '../charts/QuarterlyTestGrowthChart';
import TestAutomationStatusChart from '../charts/TestAutomationStatusChart';
import MonthlyTestCreationChart from '../charts/MonthlyTestCreationChart';
import Drill4JTestsTab from './Drill4JTestsTab';
import ReleaseExecutionTab from './ReleaseExecutionTab';
import DailyRegressionTab from './DailyRegressionTab';
import { get } from '../../services/apiService';

/**
 * AutomationTab component for displaying test automation metrics
 * @returns {JSX.Element} AutomationTab component
 */
const AutomationTab = () => {
  const [automationTab, setAutomationTab] = useState(1);
  const [growthData, setGrowthData] = useState([]);
  const [automationStatuses, setAutomationStatuses] = useState([]);
  const [suiteData, setSuiteData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleTabChange = (event, newValue) => {
    setAutomationTab(newValue);
  };

  // Fetch data from APIs
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel using public endpoints
      const [growthResult, suiteResult, summaryResult] = await Promise.all([
        get('/api/test/public/growth-trends'),
        get('/api/test/public/by-suite'),
        get('/api/test/public/automation-summary')
      ]);

      // Update state with fetched data
      setGrowthData(growthResult.data || []);
      setAutomationStatuses(growthResult.automationStatuses || []);
      setSuiteData(suiteResult.data || []);
      setSummaryData(summaryResult.data || []);
      setTestSuites(summaryResult.testSuites || []);
      setFilters(summaryResult.filters || {});

    } catch (err) {
      console.error('Error fetching automation data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes from the table
  const handleFilterChange = async (filterParams) => {
    try {
      // Build query parameters
      const queryParams = {};
      if (filterParams.testSuite && filterParams.testSuite !== 'all') {
        queryParams.testSuite = filterParams.testSuite;
      }
      if (filterParams.automationStatus && filterParams.automationStatus !== 'all') {
        queryParams.automationStatus = filterParams.automationStatus;
      }

      // Fetch filtered summary data using public endpoint
      const result = await get('/api/test/public/automation-summary', queryParams);
      setSummaryData(result.data || []);

    } catch (err) {
      console.error('Error applying filters:', err);
      setError(err.message);
    }
  };

  // Close error snackbar
  const handleCloseError = () => {
    setError(null);
  };

  // Fetch data on component mount (only for first tab)
  useEffect(() => {
    if (automationTab === 0) {
      fetchData();
    }
  }, [automationTab]);

  // Show loading spinner (only for first tab)
  if (automationTab === 0 && loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading automation metrics...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Tabs value={automationTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Automation Metrics" />
        <Tab label="Drill4J Tests" />
        <Tab label="Release Execution" />
        <Tab label="Daily Regression" />
      </Tabs>

      {automationTab === 0 && (
        <>
          <Typography variant="h6" gutterBottom>
            Test Automation Metrics
          </Typography>
          <Typography variant="body1" paragraph color="text.secondary">
            Monitor test automation progress, growth trends, and distribution across test suites.
          </Typography>
          
          <Grid container spacing={3}>
        {/* Test Growth Chart - Full Width - HIDDEN */}
        {/* <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <TestGrowthChart 
              growthData={growthData} 
              automationStatuses={automationStatuses}
            />
          </Paper>
        </Grid> */}
        
        {/* Quarterly Test Growth Chart - Full Width - HIDDEN */}
        {/* <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <QuarterlyTestGrowthChart />
          </Paper>
        </Grid> */}
        
        {/* Monthly Test Creation Chart - Full Width */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <MonthlyTestCreationChart />
          </Paper>
        </Grid>
        
        {/* Test Suite Distribution Chart - Half Width */}
        <Grid item xs={12} lg={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <TestSuiteDistributionChart suiteData={suiteData} />
          </Paper>
        </Grid>
        
        {/* Summary Stats - Half Width */}
        <Grid item xs={12} lg={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Stats
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Test Suites
                </Typography>
                <Typography variant="h4" color="primary">
                  {suiteData.length}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Test Cases
                </Typography>
                <Typography variant="h4" color="primary">
                  {suiteData.reduce((sum, item) => sum + item.count, 0)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Growth This Month
                </Typography>
                <Typography variant="h4" color="success.main">
                  {growthData.length > 0 ? growthData[growthData.length - 1]?.totalCount || 0 : 0}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        {/* Test Automation Status by Release Version - Full Width */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Test Automation Status by Release Version
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              View test case counts grouped by automation status for a specific release version.
            </Typography>
            <TestAutomationStatusChart />
          </Paper>
        </Grid>
        
        {/* Automation Status Table - Full Width */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <AutomationStatusTable 
              summaryData={summaryData}
              testSuites={testSuites}
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          </Paper>
        </Grid>
      </Grid>
        </>
      )}

      {automationTab === 1 && (
        <Drill4JTestsTab />
      )}

      {automationTab === 2 && (
        <ReleaseExecutionTab />
      )}

      {automationTab === 3 && (
        <DailyRegressionTab />
      )}

      {/* Error Snackbar */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseError} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AutomationTab;
